import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import { TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from 'LuxKit/Typography/Typography'
import { setCSSVar } from 'lib/theme/themeUtils'

/** The flight result and itinerary cards share this breakpoint and container name. */
export const FLIGHT_CARD_DESKTOP_BREAKPOINT = 640
export const flightCardDesktop = `@container chat-flight-card (min-width: ${rem(FLIGHT_CARD_DESKTOP_BREAKPOINT)})`

export const flightCardHeading6 = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '19px')}
  font-size: 16px;
`

const FlightSummary = styled.div`
  align-items: center;
  column-gap: ${rem(8)};
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  min-width: 0;
  row-gap: ${rem(8)};
  width: 100%;

  @container chat-flight-card (min-width: ${rem(450)}) {
    justify-content: flex-start;
    column-gap: ${rem(12)};
  }

  ${flightCardDesktop} {
    flex: 1;
    flex-wrap: nowrap;
    justify-content: flex-start;
    min-width: ${rem(328)};
    width: auto;
  }

  @container chat-flight-card (min-width: ${rem(720)}) {
    column-gap: ${rem(24)};
    min-width: ${rem(392)};
  }
`

const AirlineGroup = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 ${rem(148)};
  gap: ${rem(4)};
  min-width: 0;

  @container chat-flight-card (min-width: ${rem(450)}) {
    flex-basis: ${rem(180)};
  }

  ${flightCardDesktop} {
    gap: ${rem(8)};
  }
`

const AirlineLogoBox = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: ${rem(32)};
  justify-content: center;
  width: ${rem(32)};

  ${flightCardDesktop} {
    height: ${rem(38)};
    width: ${rem(38)};
  }
`

const AirlineLogoImage = styled.img`
  height: ${rem(27)};
  object-fit: contain;
  width: ${rem(27)};

  ${flightCardDesktop} {
    height: ${rem(32)};
    width: ${rem(32)};
  }
`

const StackedColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  justify-content: center;
  min-width: 0;
`

const FlightTimeRow = styled.div`
  align-items: baseline;
  display: flex;
  white-space: nowrap;
`

const FlightTimeRange = styled(Heading)`
  &&& {
    ${flightCardHeading6}
    font-size: ${rem(14)};

    @container chat-flight-card (min-width: ${rem(450)}) {
      font-size: ${rem(16)};
    }
  }
`

const ArrivalDayOffset = styled(Caption)`
  margin-inline-start: ${rem(4)};
`

const FlightMetaGroup = styled.div`
  display: grid;
  flex-shrink: 0;
  grid-template-columns: minmax(${rem(64)}, 1fr);
  gap: ${rem(8)};
  min-width: 0;
  white-space: nowrap;

  &.has-stops {
    grid-template-columns: ${rem(64)} ${rem(48)};
  }

  @container chat-flight-card (min-width: ${rem(450)}) {
    grid-template-columns: minmax(${rem(84)}, 1fr);
    gap: ${rem(12)};

    &.has-stops {
      grid-template-columns: ${rem(84)} ${rem(80)};
    }
  }

  ${flightCardDesktop} {
    flex: 1;
    min-width: ${rem(136)};

    &.has-stops {
      grid-template-columns: ${rem(84)} minmax(${rem(40)}, ${rem(80)});
    }
  }

  @container chat-flight-card (min-width: ${rem(720)}) {
    gap: ${rem(24)};
    min-width: ${rem(188)};
  }
`

interface Props {
  carrierName?: string
  logoSrc?: string
  departureTime?: string
  arrivalTime?: string
  arrivalDaysAhead?: number
  durationLabel?: string
  routeLabel?: string
  numberOfStops?: number
  layoverDetail?: string
}

function getStopsLabel(numberOfStops: number, intl: IntlShape): string {
  if (numberOfStops === 0) {
    return intl.formatMessage({
      id: 'Cvc6NejS9VBmrNzgU3V9',
      defaultMessage: 'Direct',
      description: 'Label indicating a non-stop direct flight in the trip planner drawer flight card',
    })
  }
  if (numberOfStops === 1) {
    return intl.formatMessage({
      id: 'gfP5C0rUG_rWKvBwZ6Oy',
      defaultMessage: '1 stop',
      description: 'Label for a flight with a single layover stop in the trip planner drawer flight card',
    })
  }
  return intl.formatMessage({
    id: 'TMkiPFK3ZUXBJuoTfJvb',
    defaultMessage: '{count} stops',
    description: 'Label showing the number of layover stops in the trip planner drawer flight card',
  }, { count: numberOfStops })
}

function FlightSummaryRow({
  carrierName,
  logoSrc,
  departureTime,
  arrivalTime,
  arrivalDaysAhead = 0,
  durationLabel,
  routeLabel,
  numberOfStops,
  layoverDetail,
}: Props) {
  const intl = useIntl()
  const stopsLabel = numberOfStops === undefined ? undefined : getStopsLabel(numberOfStops, intl)
  const timeRange = departureTime && arrivalTime
    ? intl.formatMessage({
        id: 'zOG3k0v44GaR0Jn64tgV',
        defaultMessage: '{departureTime} - {arrivalTime}',
        description: 'Flight departure and arrival time range in the trip planner flight drawer card',
      }, { departureTime, arrivalTime })
    : undefined
  const arrivalDayOffsetLabel = arrivalDaysAhead > 0
    ? intl.formatMessage({
        id: 'B-LRf5XhtbzyX-ftELv2',
        defaultMessage: '+{count}',
        description: 'Number of calendar days after departure that a flight arrives',
      }, { count: arrivalDaysAhead })
    : undefined

  return <FlightSummary>
    <AirlineGroup>
      <AirlineLogoBox>
        {logoSrc ? <AirlineLogoImage src={logoSrc} alt="" /> : <LinePlaneFlyIcon size="S" />}
      </AirlineLogoBox>
      <StackedColumn>
        {timeRange && <FlightTimeRow>
          <FlightTimeRange as="span" variant="heading6" colour="neutral-two">{timeRange}</FlightTimeRange>
          {arrivalDayOffsetLabel && <ArrivalDayOffset as="span" variant="medium" weight="bold" colour="neutral-two">{arrivalDayOffsetLabel}</ArrivalDayOffset>}
        </FlightTimeRow>}
        {carrierName && <Caption variant="medium" colour="neutral-three" wrap="truncate" title={carrierName}>{carrierName}</Caption>}
      </StackedColumn>
    </AirlineGroup>
    <FlightMetaGroup className={stopsLabel ? 'has-stops' : undefined}>
      <StackedColumn>
        {durationLabel && <BodyText variant="medium" colour="neutral-two">{durationLabel}</BodyText>}
        {routeLabel && <Caption variant="medium" colour="neutral-three" wrap="truncate" title={routeLabel}>{routeLabel}</Caption>}
      </StackedColumn>
      {stopsLabel && <StackedColumn>
        <BodyText variant="medium" colour="neutral-two">{stopsLabel}</BodyText>
        {layoverDetail && <Caption variant="medium" colour="neutral-three" wrap="truncate" title={layoverDetail}>{layoverDetail}</Caption>}
      </StackedColumn>}
    </FlightMetaGroup>
  </FlightSummary>
}

export default FlightSummaryRow
